import React, { useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import genesisShopImg from "../images/Genesis-Shop.png";
import certifiedPreownedImg from "../images/Certified-Preowned.png";
import stockLocatorImg from "../images/Stock-Locator.png";

const PageContainer = styled.div`
  background: #010606;
  color: #fff;
  min-height: 100vh;
`;

const Hero = styled.div`
  padding: 140px 24px 80px;
  max-width: 1100px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: block;
  color: #f5554f;
  text-decoration: none;
  font-size: 14px;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 40px;

  &:hover {
    color: #ea342e;
  }
`;

const Tag = styled.span`
  font-size: 13px;
  color: #f5554f;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  font-weight: 500;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 600;
  margin: 16px 0 24px;
  line-height: 1.1;

  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #ccc;
  max-width: 700px;
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 12px;
  margin-top: 60px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const Section = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;
  border-top: 1px solid #1a1a1a;
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 500;
  margin-bottom: 32px;
  color: #f7f8fa;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
`;

const Card = styled.div`
  background: #111;
  border-radius: 10px;
  padding: 28px;
  border: 1px solid #1e1e1e;
`;

const CardIcon = styled.div`
  font-size: 1.8rem;
  margin-bottom: 16px;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #f5554f;
  margin-bottom: 10px;
`;

const CardText = styled.p`
  font-size: 14px;
  line-height: 1.7;
  color: #aaa;
`;

const TechList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  list-style: none;
  padding: 0;
`;

const TechItem = styled.li`
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  color: #ddd;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 60px;
`;

const PrimaryBtn = styled.a`
  padding: 14px 32px;
  background: #f5554f;
  color: #fff;
  border-radius: 10px;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: background 0.2s;

  &:hover {
    background: #ea342e;
  }
`;

const AppRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  margin-bottom: 100px;

  &:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const AppTextBlock = styled.div``;

const AppImage = styled.img`
  width: 100%;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
`;

const AppName = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: #f7f8fa;
  margin-bottom: 12px;
`;

const AppDesc = styled.p`
  font-size: 15px;
  line-height: 1.8;
  color: #aaa;
`;

const AppBtn = styled.a`
  display: inline-block;
  margin-top: 16px;
  padding: 10px 24px;
  background: transparent;
  color: #f5554f;
  border: 1px solid #f5554f;
  border-radius: 8px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #f5554f;
    color: #fff;
  }
`;

const GenesisShop = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <PageContainer>
      <Navbar />
      <Hero>
        <BackLink to="/">← Back to Portfolio</BackLink>
        <Tag>Professional Work · Hyundai Australia</Tag>
        <Title>Genesis Shop</Title>
        <Subtitle>
          Updated three Hyundai e-commerce web applications — Shop, Stock
          Locator, and Certified Pre-Owned — for Genesis vehicles. Delivered a
          comprehensive frontend upgrade across the entire suite, including a
          Node 10 to Node 18 migration (Vue 2 + Webpack to Vue 3 + Vite),
          migrating third-party libraries and rewriting code syntax throughout
          the codebase to support the updated dependencies — a roughly 4–5 year
          leap in the JavaScript ecosystem.
        </Subtitle>
        <BtnRow>
          <PrimaryBtn
            href="https://www.genesis.com/au/en/shopping/shop/gv70#.html"
            target="_blank"
            rel="noreferrer"
          >
            Visit Project ↗
          </PrimaryBtn>
        </BtnRow>
        <HeroImage src={genesisShopImg} alt="Genesis Shop" />
      </Hero>

      <Section>
        <SectionTitle>What I Built</SectionTitle>
        <Grid>
          <Card>
            <CardIcon>🎨</CardIcon>
            <CardTitle>Colour Selector</CardTitle>
            <CardText>
              Implemented complex exterior and interior colour selectors,
              supporting Genesis-specific multi-tone combinations and real-time
              vehicle preview updates.
            </CardText>
          </Card>
          <Card>
            <CardIcon>⚡</CardIcon>
            <CardTitle>Webpack → Vite Migration</CardTitle>
            <CardText>
              Migrated all three applications from Webpack to Vite, significantly
              reducing build times and improving local development HMR speeds.
            </CardText>
          </Card>
          <Card>
            <CardIcon>🔼</CardIcon>
            <CardTitle>Vue 2 → Vue 3 Upgrade</CardTitle>
            <CardText>
              Upgraded the entire codebase from Vue 2 to Vue 3, including
              updating Vue Router and Vuex to their latest versions,
              upgrading third-party libraries to their Vue 3-compatible
              versions, and rewriting affected code syntax throughout the
              codebase to support the updated dependencies.
            </CardText>
          </Card>
          <Card>
            <CardIcon>💰</CardIcon>
            <CardTitle>Finance & Business Logic</CardTitle>
            <CardText>
              Updated Genesis Finance Calculator to a new API and business logic,
              supporting Genesis-specific vehicle features including selection of
              powertrain, seat, and variant, as well as accessories and
              charger options.
            </CardText>
          </Card>
          <Card>
            <CardIcon>🏗️</CardIcon>
            <CardTitle>Genesis Design System</CardTitle>
            <CardText>
              Created a Genesis design system to enforce consistent brand theming
              — typography, colour tokens, spacing, and component library — across
              Shop, Stock Locator, and CPO.
            </CardText>
          </Card>
          <Card>
            <CardIcon>🚗</CardIcon>
            <CardTitle>Three Applications</CardTitle>
            <CardText>
              Delivered updates across Shop, Stock Locator, and Certified
              Pre-Owned simultaneously, ensuring feature parity and consistent UX
              across the full Genesis buying journey.
            </CardText>
          </Card>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>The Three Applications</SectionTitle>
        <AppRow>
          <AppImage src={genesisShopImg} alt="Genesis Shop" />
          <AppTextBlock>
            <Tag>Shop</Tag>
            <AppName>Genesis Shop</AppName>
            <AppDesc>
              The primary e-commerce experience for purchasing new Genesis
              vehicles. Customers can configure their ideal vehicle by selecting
              exterior and interior colours, explore finance options, and secure
              their car by paying a deposit — all within a seamless, branded
              online journey.
            </AppDesc>
            <AppBtn
              href="https://www.genesis.com/au/en/shopping/shop/gv70#.html"
              target="_blank"
              rel="noreferrer"
            >
              Visit Site ↗
            </AppBtn>
          </AppTextBlock>
        </AppRow>
        <AppRow>
          <AppTextBlock>
            <Tag>Certified Pre-Owned</Tag>
            <AppName>Certified Pre-Owned</AppName>
            <AppDesc>
              Customers can browse and purchase pre-owned Genesis vehicles
              through the official Genesis channel. Each listed vehicle has been
              certified by Genesis, giving buyers confidence in quality.
              Customers can secure the car by paying a deposit directly through
              the platform.
            </AppDesc>
            <AppBtn
              href="https://www.genesis.com/au/en/shopping/certified-pre-owned.html/"
              target="_blank"
              rel="noreferrer"
            >
              Visit Site ↗
            </AppBtn>
          </AppTextBlock>
          <AppImage src={certifiedPreownedImg} alt="Certified Pre-Owned" />
        </AppRow>
        <AppRow>
          <AppImage src={stockLocatorImg} alt="Stock Locator" />
          <AppTextBlock>
            <Tag>Stock Locator</Tag>
            <AppName>Stock Locator</AppName>
            <AppDesc>
              Allows customers to search for older Genesis models available in
              stock by entering their postcode, surfacing vehicles available for
              delivery in their area. Once a vehicle is found, the customer is
              redirected to the Genesis Shop website to complete the purchase and
              pay a deposit.
            </AppDesc>
            <AppBtn
              href="https://www.genesis.com/au/en/shopping/new-vehicle-locator.html/"
              target="_blank"
              rel="noreferrer"
            >
              Visit Site ↗
            </AppBtn>
          </AppTextBlock>
        </AppRow>
      </Section>

      <Section>
        <SectionTitle>Tech Stack</SectionTitle>
        <TechList>
          {[
            "Vue 3",
            "TypeScript",
            "Vite",
            "SCSS",
            "Options API",
            "Design Systems",
            "Vuex",
            "Vue Router"
          ].map((tech) => (
            <TechItem key={tech}>{tech}</TechItem>
          ))}
        </TechList>
      </Section>

      <Footer />
    </PageContainer>
  );
};

export default GenesisShop;
